import { useEffect, useState } from "react";

import { integrationApiInstance } from "../apis/integrationApi";
import SupervisorApi from "../apis/supervisorApi";

const supervisorApi = new SupervisorApi();

export interface AgentFilterQueueOption {
  id: number;
  name: string;
  kind: "queue" | "category";
}

/** Default completion time-entry minutes per Theo L1 category, from the taxonomy response. */
export interface TimeEntryDefaultMinutes {
  uncategorized: number;
  categories: Record<string, number>;
}

function normalizeTimeEntryDefaults(response: unknown): TimeEntryDefaultMinutes | null {
  const raw = (response as { time_entry_defaults?: unknown })?.time_entry_defaults;
  if (!raw || typeof raw !== "object") return null;
  const record = raw as { uncategorized?: unknown; categories?: unknown };
  const uncategorized = Number(record.uncategorized);
  if (!Number.isInteger(uncategorized)) return null;
  const categories: Record<string, number> = {};
  if (record.categories && typeof record.categories === "object") {
    for (const [l1, value] of Object.entries(record.categories as Record<string, unknown>)) {
      const minutes = Number(value);
      if (Number.isInteger(minutes)) categories[l1] = minutes;
    }
  }
  return { uncategorized, categories };
}

export interface AgentFilterTaxonomyOption {
  label: string;
  l1: string;
  l2: string | null;
}

function normalizeQueueOptions(items: unknown, kind: AgentFilterQueueOption["kind"]): AgentFilterQueueOption[] {
  const rows = Array.isArray(items) ? items : [];
  const options: AgentFilterQueueOption[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const id = Number(record.groupId ?? record.id);
    const name = String(record.name ?? "").trim();
    if (!Number.isFinite(id) || id <= 0 || !name) continue;
    if (options.some((option) => option.id === id)) continue;
    options.push({ id, name, kind });
  }
  return options.sort((left, right) => left.name.localeCompare(right.name));
}

function normalizeTaxonomyOptions(response: unknown): AgentFilterTaxonomyOption[] {
  const categories = Array.isArray((response as { categories?: unknown })?.categories)
    ? (response as { categories: unknown[] }).categories
    : [];
  const options: AgentFilterTaxonomyOption[] = [];
  for (const category of categories) {
    if (!category || typeof category !== "object") continue;
    const record = category as Record<string, unknown>;
    const l1 = String(record.l1 ?? "").trim();
    if (!l1) continue;
    options.push({ label: l1, l1, l2: null });
    const l2Rows = Array.isArray(record.l2) ? record.l2 : [];
    for (const l2 of l2Rows) {
      const leaf = String(l2 ?? "").trim();
      if (!leaf) continue;
      options.push({ label: `${l1} > ${leaf}`, l1, l2: leaf });
    }
  }
  return options;
}

/**
 * Option lists for the agent Step 2 filters, from the same sources the
 * Auto Mode screens use: PSA queues and ticket categories from the supervisor
 * APIs, Theo ticket taxonomy from the integration API. A filter line the
 * agent already stores but which is absent here (a typed-in id) still renders
 * in the queue dropdown, so nothing is ever lost.
 */
export function useAgentFilterOptions() {
  const [queueOptions, setQueueOptions] = useState<AgentFilterQueueOption[]>([]);
  const [taxonomyOptions, setTaxonomyOptions] = useState<AgentFilterTaxonomyOption[]>([]);
  const [timeEntryDefaults, setTimeEntryDefaults] = useState<TimeEntryDefaultMinutes | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      supervisorApi.getTechnicianGroupList().catch(() => []),
      supervisorApi.getTicketCategoryList().catch(() => []),
      integrationApiInstance.getTheoTicketTaxonomy().catch(() => ({ categories: [] })),
    ]).then(([queueRows, categoryRows, taxonomyResponse]) => {
      if (!active) return;
      setQueueOptions([
        ...normalizeQueueOptions(queueRows, "queue"),
        ...normalizeQueueOptions(categoryRows, "category"),
      ]);
      setTaxonomyOptions(normalizeTaxonomyOptions(taxonomyResponse));
      setTimeEntryDefaults(normalizeTimeEntryDefaults(taxonomyResponse));
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { queueOptions, taxonomyOptions, timeEntryDefaults, loading };
}
